import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import { VideoData } from '@lumen5/luminary';
import type { ChartOptions } from '@lumen5/luminary/core/asset/ChartAsset/types';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import type { CustomRendererComponentProps, ToolDefinition, ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import {
    applyGeneratedChartConfigOperation,
    getDefaultChartConfig,
    setChartTypeOperation,
} from 'js/src/libs/chart-asset-add';
import { SUPPORTED_CHART_TYPES, type SupportedChartType } from 'js/src/libs/default-chart-configs';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

interface EditChartArgs {
    scene_id: string;
    asset_name: string;
    data: string;
    chart_type: SupportedChartType;
}

export class EditChartTool extends VideoStateTool<EditChartArgs> {
    public static readonly toolName = 'edit_chart';

    public override customRenderer = EditChartResultRenderer;

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: EditChartTool.toolName,
                description:
                    'Edit an existing chart on a scene by regenerating it. Works on charts that came in from a chart scene design and on charts added by the chart tools; find the chart in the video state (type CHART) to get its scene_id and asset_name. An edit fully re-specifies the chart, so you MUST provide BOTH chart_type and data every time — even when only one is changing — because the data replaces the whole dataset.',
                parameters: {
                    type: 'object',
                    properties: {
                        scene_id: {
                            type: 'string',
                            description: 'The scene_id from the video state.',
                        },
                        asset_name: {
                            type: 'string',
                            description: 'The name of the chart asset (e.g. chart_1)',
                        },
                        data: {
                            type: 'string',
                            description:
                                "Required. The full data the chart should show, described with the actual numbers/labels inline. Replaces the chart's data entirely, so pass every point (not just the part you are changing). When only changing the type, restate the chart's current values here.",
                        },
                        chart_type: {
                            type: 'string',
                            description:
                                "Required. The chart type after the edit (bar/line/pie) — the chart's current type if only fixing data, or the new type to switch it.",
                            enum: [...SUPPORTED_CHART_TYPES],
                        },
                    },
                    required: ['scene_id', 'asset_name', 'data', 'chart_type'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Edit Chart';
    }

    public get statusIcon(): string {
        return 'bar_chart';
    }

    public async execute(args: EditChartArgs, context: ToolExecutionContext) {
        const { asset_name, data, chart_type } = args;
        const { parentActionGroup } = context;

        try {
            if (!data.trim()) {
                return 'Error: data is required — describe the full data the chart should show.';
            }

            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }
            if (!VideoData.isChartAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a chart`;
            }

            const { setChartType, applyGeneratedChartConfig } = makeOperationsCallable(
                {
                    setChartTypeOperation,
                    applyGeneratedChartConfigOperation,
                },
                this.luminary,
                this.dispatch,
                this._getState,
            );

            const changes: string[] = [];
            const typeChanged = chart_type !== asset.chartType;

            if (typeChanged) {
                setChartType(scene.id, asset.id, chart_type);
                changes.push(`type to ${chart_type}`);
            }

            // An edit always regenerates from the provided data. On a type change we regenerate
            // against the NEW type's default styled config (a clean reset onto that type's look);
            // otherwise we preserve the chart's current config (style-preserving refresh). The
            // regenerate prompt drops surplus template series so the data isn't crammed into extras.
            const baseConfigOverride = typeChanged
                ? (getDefaultChartConfig(chart_type, 0)?.echarts as ChartOptions | undefined)
                : undefined;
            const status = await applyGeneratedChartConfig({
                sceneId: scene.id,
                assetId: asset.id,
                chartType: chart_type,
                data,
                baseConfigOverride,
                extractChartDataFromResult: false,
                parentActionGroup,
            });
            if (status !== 'success') {
                return `Error: Could not regenerate chart data for ${asset_name} in scene ${sceneShortId}`;
            }
            changes.push('data');

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            return `Success: Updated ${changes.join(', ')} for chart ${asset_name} in scene ${sceneShortId}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error editing chart: ${errorMessage}`;
        }
    }
}

const EditChartResultRenderer: React.FC<CustomRendererComponentProps> = ({ isLoading, result }) => {
    if (isLoading) {
        return null;
    }
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
        </div>
    );
};
